import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { Observable, interval, of, throwError } from 'rxjs';
import { catchError, filter, mergeMap, take } from 'rxjs/operators';
import {
  AdminOrganization,
  AdminOrganizationQuery,
  AdminOrganizationsWithMetadata,
} from 'src/app/admin/models/organization.model';
import {
  AdminOrganizationApiError,
  AdminOrganizationApiErrorTypes,
} from './admin-organization.errors';
import { toHttpParams } from '../project/helpers/to-http-params';
import {
  UndeployAllProjectsRequest,
  UndeployAllProjectsTask,
} from 'src/app/admin/models/task.model';
import { RETRY_DELAY } from '../config/config.service';

const BASE_URL = '/admin/organizations';

@Injectable()
export class AdminOrganizationService {
  constructor(private http: HttpClient) {}

  loadAdminOrganizations(
    query: AdminOrganizationQuery,
  ): Observable<AdminOrganizationsWithMetadata> {
    const { includeDeleted, page, limit, sortOrder, orderBy, search } = query;
    const url = `${environment.apiOrg.urlV2}${BASE_URL}`;
    const params = {
      includeDeleted,
      page,
      limit,
      sortOrder,
      orderBy,
      search,
    };

    return this.http
      .get<AdminOrganizationsWithMetadata>(url, { params })
      .pipe(
        catchError((err) =>
          throwError(
            new AdminOrganizationApiError(
              AdminOrganizationApiErrorTypes.AdminLoadOrganizationsError,
              err,
            ),
          ),
        ),
      );
  }

  // split from `loadAdminOrganizations` into separate method as responseType throws an error
  // https://stackoverflow.com/questions/46408537/angular-httpclient-http-failure-during-parsing/69582514#69582514
  loadAdminOrganizationsCsv(query?: AdminOrganizationQuery): Observable<Blob> {
    const url = `${environment.apiOrg.url}${BASE_URL}`;
    const queryFilter = this.getFilter(query);
    const params = toHttpParams({ filter: queryFilter, dataFormat: 'csv' });

    return this.http
      .get(url, {
        params,
        headers: { Accept: 'application/octet-stream' },
        responseType: 'blob',
      })
      .pipe(
        catchError((err) =>
          throwError(
            new AdminOrganizationApiError(
              AdminOrganizationApiErrorTypes.AdminLoadOrganizationsCsvError,
              err,
            ),
          ),
        ),
      );
  }

  getAdminOrganization(id: number): Observable<AdminOrganization> {
    const url = `${environment.apiOrg.url}${BASE_URL}/${id}`;

    return this.http
      .get<AdminOrganization>(url)
      .pipe(
        catchError((err) =>
          throwError(
            new AdminOrganizationApiError(
              AdminOrganizationApiErrorTypes.AdminOrganizationNotFoundError,
              err,
            ),
          ),
        ),
      );
  }

  addAdminOrganization(
    name: string,
    displayName: string,
    description: string,
    organizationType: string,
    addDefaultConfig: boolean,
    addStockDPS: boolean,
  ): Observable<AdminOrganization> {
    const url = `${environment.apiOrg.url}${BASE_URL}?initWithDefaultConfig=${addDefaultConfig}&initWithStocks=${addStockDPS}`;
    const data = { name, displayName, description, organizationType };

    return this.http.post<AdminOrganization>(url, data).pipe(
      catchError((err) => {
        if (err.status === 409) {
          return throwError(
            new AdminOrganizationApiError(
              AdminOrganizationApiErrorTypes.AdminConflictOrganizationError,
              new Error(err.error.error.message),
            ),
          );
        }
        return throwError(
          new AdminOrganizationApiError(
            AdminOrganizationApiErrorTypes.AdminCreateOrganizationError,
            err,
          ),
        );
      }),
    );
  }

  updateAdminOrganization(id: number, organization: Partial<AdminOrganization>) {
    const url = `${environment.apiOrg.url}${BASE_URL}/${id}`;
    const { displayName, description, organizationType, stripeSubscriptionId } = organization;

    return this.http
      .patch<AdminOrganization>(url, {
        displayName,
        description,
        organizationType,
        stripeSubscriptionId,
      })
      .pipe(
        catchError((err) => {
          if (err.status === 409) {
            return throwError(
              new AdminOrganizationApiError(
                AdminOrganizationApiErrorTypes.AdminConflictOrganizationError,
                new Error(err.error.error.message),
              ),
            );
          }
          return throwError(
            new AdminOrganizationApiError(
              AdminOrganizationApiErrorTypes.AdminUpdateOrganizationError,
              err,
            ),
          );
        }),
      );
  }

  deleteAdminOrganization(id: number): Observable<any> {
    const url = `${environment.apiOrg.url}${BASE_URL}/${id}`;

    return this.http
      .delete(url)
      .pipe(
        catchError((err) =>
          throwError(
            new AdminOrganizationApiError(
              AdminOrganizationApiErrorTypes.AdminDeleteOrganizationError,
              err,
            ),
          ),
        ),
      );
  }

  hardDeleteAdminOrganization(id: number): Observable<any> {
    const url = `${environment.apiOrg.url}${BASE_URL}/${id}/hard`;

    return this.http
      .delete(url)
      .pipe(
        catchError((err) =>
          throwError(
            new AdminOrganizationApiError(
              AdminOrganizationApiErrorTypes.AdminHardDeleteOrganizationError,
              err,
            ),
          ),
        ),
      );
  }

  public undeployAllProjectsInOrganization(id: number): Observable<UndeployAllProjectsTask> {
    const url = `${environment.apiConfig.url}/admin/tasks`;
    const data: UndeployAllProjectsRequest = {
      type: 'UNDEPLOY_ALL',
      organizationId: id,
    };
    return this.http.post<UndeployAllProjectsTask>(url, data);
  }

  public pollUndeployAllProjectsInOrganizationStatus(
    taskId: number,
  ): Observable<UndeployAllProjectsTask> {
    return this.pollTaskStatus<UndeployAllProjectsTask>(taskId);
  }

  private getTaskStatus<Task extends UndeployAllProjectsTask>(taskId: number): Observable<Task> {
    const url = `${environment.apiConfig.url}/tasks/${taskId}`;
    return this.http
      .get<Task>(url)
      .pipe(
        mergeMap((task) =>
          task.status === 'ERROR' ? throwError(new Error('Undeploy All Projects Error')) : of(task),
        ),
      );
  }

  private pollTaskStatus<Task extends UndeployAllProjectsTask>(taskId: number): Observable<Task> {
    return interval(RETRY_DELAY).pipe(
      mergeMap(() => this.getTaskStatus<Task>(taskId)),
      filter(({ status }) => status === 'DONE'),
      take(1),
      catchError((err) =>
        throwError(
          new AdminOrganizationApiError(
            AdminOrganizationApiErrorTypes.AdminUndeployAllProjectsInOrganizationError,
            err,
          ),
        ),
      ),
    );
  }

  private getFilter(query?: AdminOrganizationQuery) {
    return query
      ? JSON.stringify({
          where: {
            ...(query?.includeDeleted ? null : { datetimeDeleted: null }),
          },
        })
      : undefined;
  }
}
